/**
 * The two runs of a level, on the clear (lib/agent-first.ts, owner
 * 2026-10-28): the learner's agent solves the level as it is; the clear shows
 * what that run cost, its tokens, what drove the cost and the tips; the
 * learner applies one, starts a fresh agent and runs the same level again; and
 * the clear after that shows what the change saved.
 *
 * Which of the two it draws is the meter's own comparison
 * (`practiceComparison`): no runs after the first pass yet is the first
 * screen, and any is the second. Every figure is the learner's own, priced
 * with the meter's book, and a run that cost more says so without confetti.
 * It draws nothing for a pass by hand, a guest, or while feedback is hidden,
 * exactly as WhatItCost does.
 */
import { useEffect, useState } from 'react';
import type { AttemptFeedback, RunUsage, Technique } from '@engineer-os/engine';
import { fetchFeedback, runDifference, type RunFigures } from '../../lib/efficiency';
import { intlTag } from '../../lib/format';
import { levelAdvice, type LevelAdvice } from '../../lib/level-advice';
import { twoRunsCopy } from '../../lib/strings/two-runs';
import { useLocale, useStrings } from '../../state/useLocale';
import { Button } from '../../ui';
import { Copy } from '../agent/AgentKeys';
import { usePassFeedback } from './WhatItCost';

/** The tips offered after the first run, in the order they are worth trying. */
const TIPS: Technique[] = ['right-size-model', 'restart', 'scope-context'];

export function TwoRuns({ open, missionId, signedIn, onReplay }: {
  open: boolean;
  missionId: string;
  signedIn: boolean;
  onReplay(): void;
}) {
  const feedback = usePassFeedback(open, missionId, signedIn);
  // The first run's own figures, for the comparison on the second screen.
  const firstId = feedback?.practice?.after ? feedback.practice.before.passId : null;
  const [first, setFirst] = useState<RunFigures | null>(null);
  useEffect(() => {
    setFirst(null);
    if (!firstId) return undefined;
    let live = true;
    void fetchFeedback(firstId).then((f) => {
      if (live && f?.usage) setFirst({ costMicro: f.attempt.costMicro, usage: f.usage });
    });
    return () => { live = false; };
  }, [firstId]);
  return feedback ? <TwoRunsPanel feedback={feedback} first={first} onReplay={onReplay} /> : null;
}

/** The panel from feedback in hand; exported for the tests. */
export function TwoRunsPanel({ feedback, first = null, onReplay }: { feedback: AttemptFeedback; first?: RunFigures | null; onReplay(): void }) {
  const { locale } = useLocale();
  const s = twoRunsCopy[locale];
  const tag = intlTag(locale);
  const usd = (micro: number) => new Intl.NumberFormat(tag, { style: 'currency', currency: 'USD', minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(micro / 1e6);
  const pct = (x: number) => new Intl.NumberFormat(tag, { style: 'percent', maximumFractionDigits: 0 }).format(x);
  const practice = feedback.practice;

  if (practice?.after) {
    const before = practice.before.costMicro;
    const after = practice.after.costMicro;
    const saved = before - after;
    const share = before > 0 ? Math.abs(saved) / before : 0;
    const latest = feedback.usage ? { costMicro: feedback.attempt.costMicro, usage: feedback.usage } : null;
    const diff = first && latest ? runDifference(first, latest) : null;
    // A run that kept an earlier session is not the fresh agent the comparison is about: no confetti for it.
    const celebrate = saved > 0 && !diff?.carriedOver;
    const n = (x: number) => new Intl.NumberFormat(tag).format(x);
    return (
      <section className={`two-runs two-runs--result${celebrate ? ' is-saved' : ''}`} aria-labelledby="two-runs-result">
        {celebrate && (
          <span className="two-runs__confetti" aria-hidden="true">
            {Array.from({ length: 28 }, (_, i) => (
              <span key={i} style={{ ['--x' as string]: `${(i * 37) % 100}%`, ['--t' as string]: `${(i % 7) * 80}ms` }} />
            ))}
          </span>
        )}
        <h3 className="label" id="two-runs-result">{saved > 0 ? s.savedTitle : saved < 0 ? s.moreTitle : s.runAfter}</h3>
        {saved > 0 ? (
          <>
            <p className="two-runs__headline">{usd(saved)}</p>
            <p className="two-runs__sub">{s.lessThan(pct(share))}</p>
          </>
        ) : (
          <p>{saved < 0 ? s.moreThan(pct(share)) : s.same}</p>
        )}
        {diff?.carriedOver && first && latest && (
          <p className="two-runs__warn">{s.carriedOver(n(latest.usage.firstInputTokens), n(first.usage.firstInputTokens))}</p>
        )}
        <dl className="two-runs__pair">
          <div><dt>{s.before}</dt><dd>{usd(before)}</dd></div>
          <div><dt>{s.after}</dt><dd>{usd(after)}</dd></div>
        </dl>
        {first && latest && diff ? <Difference first={first} latest={latest} diff={diff} /> : feedback.usage && <Usage usage={feedback.usage} />}
        {saved > 0 && (
          <>
            <p>{s.monthly(new Intl.NumberFormat(tag, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(100 * (after / before)))}</p>
            <p className="note">{s.monthlyNote}</p>
          </>
        )}
        <p className="note">{s.honest} {s.varies}</p>
        <p className="note">{s.priced(practice.book)}{practice.reported ? ` ${s.reported}` : ''}</p>
        {(saved <= 0 || diff?.carriedOver) && <Button variant="solid" wide onClick={onReplay}>{s.replay}</Button>}
      </section>
    );
  }

  const usage = feedback.usage;
  const advice = levelAdvice(feedback.attempt.ref);
  return (
    <section className="two-runs" aria-labelledby="two-runs-first">
      <h3 className="label" id="two-runs-first">{s.run1}</h3>
      <p className="two-runs__headline">{usd(feedback.attempt.costMicro)}</p>
      <p className="note">{s.priced(feedback.attempt.book)}{feedback.attempt.reported ? ` ${s.reported}` : ''}</p>
      {usage && <Usage usage={usage} />}
      {usage && <Analysis usage={usage} total={feedback.attempt.costMicro} />}
      {advice ? <Measured missionId={feedback.attempt.ref} advice={advice} /> : <Tips pointed={feedback.technique} parModel={feedback.parModel ?? null} />}
      <h4 className="label two-runs__h4">{s.stepsTitle}</h4>
      <ol className="two-runs__steps">
        <li>{!advice ? s.step1
          : (advice.verdict ?? advice.measured[0]!.change) === 'smaller-model' ? s.stepModel
            : (advice.verdict ?? advice.measured[0]!.change) === 'focused-request' ? s.stepRequest
              : s.stepKeep}</li>
        <li>{s.step2}</li>
        <li>{s.step3}</li>
      </ol>
      <Button variant="solid" wide onClick={onReplay}>{s.replay}</Button>
    </section>
  );
}

/** Both runs side by side, and the one sentence that says what made the change: work, price per token, or both. */
function Difference({ first, latest, diff }: { first: RunFigures; latest: RunFigures; diff: { tokenRatio: number; priceRatio: number } }) {
  const { locale } = useLocale();
  const s = twoRunsCopy[locale];
  const tag = intlTag(locale);
  const n = (x: number) => new Intl.NumberFormat(tag).format(x);
  const pct = (x: number) => new Intl.NumberFormat(tag, { style: 'percent', maximumFractionDigits: 0 }).format(x);
  const signed = (x: number) => new Intl.NumberFormat(tag, { style: 'percent', maximumFractionDigits: 0, signDisplay: 'exceptZero' }).format(x);
  // Per million tokens, the way providers price them: per thousand rounds to nothing.
  const perMillion = (r: RunFigures) => new Intl.NumberFormat(tag, { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
    .format(r.costMicro / (r.usage.inputTokens + r.usage.outputTokens));
  const calls = (u: RunUsage) => u.models.reduce((sum, m) => sum + m.calls, 0);
  const { tokenRatio: t, priceRatio: p } = diff;
  const sentence = t >= 1.05 && p < 0.95 ? s.modelOnly(pct(t - 1), pct(1 - p))
    : t < 0.95 && Math.abs(p - 1) < 0.05 ? s.lessWork(pct(1 - t))
      : t < 0.95 && p < 0.95 ? s.both(pct(1 - t), pct(1 - p))
        : s.mixed(signed(t - 1), signed(p - 1));
  const rows: [string, string, string][] = [
    [s.tokensIn, n(first.usage.inputTokens), n(latest.usage.inputTokens)],
    [s.tokensOut, n(first.usage.outputTokens), n(latest.usage.outputTokens)],
    [s.calls, n(calls(first.usage)), n(calls(latest.usage))],
    [s.perMillion, perMillion(first), perMillion(latest)],
  ];
  return (
    <>
      <h4 className="label two-runs__h4">{s.differenceTitle}</h4>
      <table className="two-runs__table">
        <thead>
          <tr>
            <th scope="col" />
            {/* Each run's main model under its name: machine text, left to right in every language. */}
            <th scope="col">{s.firstRun}<small className="machine" dir="ltr">{first.usage.models[0]?.model}</small></th>
            <th scope="col">{s.latestRun}<small className="machine" dir="ltr">{latest.usage.models[0]?.model}</small></th>
          </tr>
        </thead>
        <tbody>{rows.map(([label, a, b]) => <tr key={label}><th scope="row">{label}</th><td>{a}</td><td>{b}</td></tr>)}</tbody>
      </table>
      <p className="two-runs__why">{sentence}</p>
    </>
  );
}

function Usage({ usage }: { usage: RunUsage }) {
  const { locale } = useLocale();
  const s = twoRunsCopy[locale];
  const tag = intlTag(locale);
  const n = (x: number) => new Intl.NumberFormat(tag).format(x);
  const usd = (micro: number) => new Intl.NumberFormat(tag, { style: 'currency', currency: 'USD', minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(micro / 1e6);
  const calls = usage.models.reduce((sum, m) => sum + m.calls, 0);
  return (
    <>
      <dl className="two-runs__figures">
        <div><dt>{s.tokensIn}</dt><dd>{n(usage.inputTokens)}</dd>{usage.cachedTokens > 0 && <small>{s.cached(n(usage.cachedTokens))}</small>}</div>
        <div><dt>{s.tokensOut}</dt><dd>{n(usage.outputTokens)}</dd></div>
        <div><dt>{s.calls}</dt><dd>{n(calls)}</dd></div>
      </dl>
      <h4 className="label two-runs__h4">{s.byModel}</h4>
      <ul className="two-runs__models">
        {usage.models.map((m) => (
          <li key={m.model}>
            {/* A model id is machine text and reads left to right in every language. */}
            <span className="machine" dir="ltr">{m.model}</span>
            <span>{n(m.calls)}×</span>
            <span>{usd(m.costMicro)}</span>
          </li>
        ))}
      </ul>
    </>
  );
}

/** Three plain readings of the counts. Nothing here guesses at what was asked. */
function Analysis({ usage, total }: { usage: RunUsage; total: number }) {
  const { locale } = useLocale();
  const s = twoRunsCopy[locale];
  const tag = intlTag(locale);
  const pct = (x: number) => new Intl.NumberFormat(tag, { style: 'percent', maximumFractionDigits: 0 }).format(x);
  const lines: string[] = [];
  const top = usage.models[0];
  if (top && total > 0) lines.push(s.topModel(top.model, pct(Math.min(1, top.costMicro / total))));
  if (usage.inputTokens > 0 && usage.cachedTokens > 0) lines.push(s.cacheShare(pct(usage.cachedTokens / usage.inputTokens)));
  if (usage.outputTokens > 0 && usage.inputTokens >= 10 * usage.outputTokens) {
    lines.push(s.readRatio(new Intl.NumberFormat(tag, { maximumFractionDigits: 0 }).format(usage.inputTokens / usage.outputTokens)));
  }
  if (lines.length === 0) return null;
  return (
    <>
      <h4 className="label two-runs__h4">{s.analysis}</h4>
      <ul className="two-runs__analysis">{lines.map((line) => <li key={line}>{line}</li>)}</ul>
    </>
  );
}

/** A Real Task's own evidence: both changes our lab tried on it, the better first, why, and when that carries over. */
function Measured({ missionId, advice }: { missionId: string; advice: LevelAdvice }) {
  const { locale } = useLocale();
  const s = twoRunsCopy[locale];
  const pct = (x: number) => new Intl.NumberFormat(intlTag(locale), { style: 'percent', maximumFractionDigits: 1 }).format(x);
  return (
    <>
      <h4 className="label two-runs__h4">{s.tipsTitle}</h4>
      <div className="two-runs__tip">
        <p className="note"><strong>{s.measuredTitle}</strong></p>
        <p className="note">{s.measuredNote}</p>
        <ul className="two-runs__measured">
          {advice.measured.map((m, i) => {
            // Best first, unless the verdict is to keep the model: then neither change is the one to make.
            const best = i === 0 && advice.verdict !== 'keep-model';
            const result = [m.saved === null ? null : m.saved >= 0 ? s.saved(pct(m.saved)) : s.dearer(pct(-m.saved)), m.failed ? s.failedRuns(m.failed, m.runs ?? m.failed) : null, m.inConversation ? s.inConversation : null]
              .filter(Boolean).join('; ');
            return (
              <li key={m.change} className={best ? 'is-best' : undefined}>
                {best ? <strong>{s.change[m.change]}</strong> : s.change[m.change]}
                <span>{result}</span>
              </li>
            );
          })}
        </ul>
        <p>{s.why[missionId] ?? advice.why}</p>
        <p><strong>{s.useWhen[missionId] ?? advice.useWhen}</strong></p>
        {advice.request && (
          <>
            <p className="note">{s.requestLabel}</p>
            <div className="aikey__codewrap">
              <pre dir="ltr" className="aikey__code"><code>{advice.request}</code></pre>
              <Copy text={advice.request} />
            </div>
          </>
        )}
      </div>
    </>
  );
}

function Tips({ pointed, parModel }: { pointed: Technique | null; parModel: string | null }) {
  const { locale } = useLocale();
  const s = twoRunsCopy[locale];
  const e = useStrings().efficiency;
  const first = pointed ?? TIPS[0]!;
  const rest = TIPS.filter((t) => t !== first);
  const lesson = e.techniques[first];
  return (
    <>
      <h4 className="label two-runs__h4">{s.tipsTitle}</h4>
      <div className="two-runs__tip">
        {pointed && <p className="note">{s.pointed}</p>}
        <p><strong>{lesson.name}.</strong> {lesson.line}</p>
        {first === 'right-size-model' && parModel && <p>{e.clear.tryModel(parModel)}</p>}
        <p className="note">{lesson.body}</p>
      </div>
      <details className="two-runs__more">
        <summary>{s.more}</summary>
        <p className="note">{s.moreNote}</p>
        <ul>{rest.map((t) => <li key={t}><strong>{e.techniques[t].name}.</strong> {e.techniques[t].line}</li>)}</ul>
      </details>
    </>
  );
}
